import React, { useEffect, useState } from 'react';
import TrainingDetailsModal from './TrainingDetailsModal';

const CalendarView = ({ trainings, currentDate, userRole, selectedScheme, selectedInstitute }) => {
  // State for modal - RENAMED to avoid conflict
  const [clickedInstitute, setClickedInstitute] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [dateTrainings, setDateTrainings] = useState([]);
  const [showListModal, setShowListModal] = useState(false);

  // Institute color mappings for calendar with improved colors
  const instituteColors = {
    'SIR': 'bg-gradient-to-r from-blue-400 to-blue-300 text-white shadow-sm',
    'RIR': 'bg-gradient-to-r from-green-500 to-green-400 text-white shadow-sm',
    'DRC': 'bg-gradient-to-r from-purple-500 to-purple-400 text-white shadow-sm',
    'default': 'bg-gradient-to-r from-gray-400 to-gray-300 text-white shadow-sm'
  };

  // Add this helper function after your state declarations
  const filterDataByScheme = (data, selectedScheme) => {
    if (!data || data.length === 0) return [];
    if (selectedScheme === 'ALL') return data;
    return data.filter(item => item.scheme === selectedScheme);
  };

  // Add this helper function after filterDataByScheme
  const filterDataByInstitute = (data, selectedInstitute) => {
    if (!data || data.length === 0) return [];
    if (selectedInstitute === 'ALL') return data;
    return data.filter(item => item.institute === selectedInstitute);
  };

  const getInstituteColorClass = (institute) => {
    return instituteColors[institute] || instituteColors.default;
  };

  // Get formatted month and year string
  const getCurrentMonthYear = () => {
    return currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  // Format date for checking against training dates
  const formatDateString = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Handle institute click to open modal
  const handleInstituteClick = (institute, date, dayYear, dayMonth, day) => {
    const formattedDate = formatDateString(dayYear, dayMonth, day);
    
    // Filter trainings by scheme and institute first, then by date and clicked institute
    let baseFilteredTrainings = trainings;

    // Apply scheme filter if selected
    if (selectedScheme) {
      baseFilteredTrainings = filterDataByScheme(baseFilteredTrainings, selectedScheme);
    }

    // Apply institute filter if selected (this is the institute selector, not the clicked institute)
    if (selectedInstitute) {
      baseFilteredTrainings = filterDataByInstitute(baseFilteredTrainings, selectedInstitute);
    }

    // Now filter by date and clicked institute
    const filteredTrainings = baseFilteredTrainings.filter(training => {
      if (training.batchesData && training.batchesData.length > 0) {
        return training.batchesData.some(batch => 
          batch.fromDate === formattedDate && training.institute === institute
        );
      }
      return false;
    });
    
    // FIXED: Use the renamed state variable
    setClickedInstitute(institute);
    setSelectedDate(formattedDate);
    setDateTrainings(filteredTrainings);
    
    // Always show the list modal first, even for a single training
    setSelectedTraining(null);
    setShowListModal(true);
    setModalIsOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalIsOpen(false);
    setSelectedTraining(null);
    setShowListModal(false);
  };

  // Handle approve and reject actions (placeholders)
  const handleApprove = () => {
    // Implement approval logic
    handleCloseModal();
  };

  const handleReject = () => {
    // Implement rejection logic
    handleCloseModal();
  };

  // Select a specific training from the list
  const handleSelectTraining = (training) => {
    setSelectedTraining(training);
    setShowListModal(false);
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // First day of the month (0 = Sunday, 1 = Monday, etc.)
    const firstDay = new Date(year, month, 1).getDay();
    
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0).getDate();
    
    // Calculate days from current month only
    const currentMonthDays = [];
    for (let i = 1; i <= lastDay; i++) {
      currentMonthDays.push(i);
    }
    
    // Filter trainings by selected scheme and institute
    let filteredTrainings = trainings;

    // Apply scheme filter first if scheme is selected
    if (selectedScheme) {
      filteredTrainings = filterDataByScheme(filteredTrainings, selectedScheme);
    }

    // Apply institute filter if institute is selected
    if (selectedInstitute) {
      filteredTrainings = filterDataByInstitute(filteredTrainings, selectedInstitute);
    }
    
    // Create a mapping of dates to trainings, grouped by institute
    const dateInstitutesMap = {};
    
    if (filteredTrainings.length > 0) {
      filteredTrainings.forEach(training => {
        if (training.batchesData && training.batchesData.length > 0) {
          training.batchesData.forEach(batch => {
            const fromDate = batch.fromDate;
            if (!dateInstitutesMap[fromDate]) {
              dateInstitutesMap[fromDate] = {};
            }
            
            const institute = training.institute;
            if (!dateInstitutesMap[fromDate][institute]) {
              dateInstitutesMap[fromDate][institute] = 0;
            }
            
            // Increment count for this institute on this date
            dateInstitutesMap[fromDate][institute]++;
          });
        }
      });
    }
    
    // Generate weeks array for rendering - only with current month days
    const weeks = [];
    let dayIndex = 0;
    
    // For the first week, add empty cells for days before month starts
    const firstWeek = Array(7).fill(null);
    for (let i = firstDay; i < 7; i++) {
      if (dayIndex < currentMonthDays.length) {
        firstWeek[i] = currentMonthDays[dayIndex++];
      }
    }
    weeks.push(firstWeek);
    
    // Add remaining weeks with only current month days
    while (dayIndex < currentMonthDays.length) {
      const week = Array(7).fill(null);
      for (let i = 0; i < 7 && dayIndex < currentMonthDays.length; i++) {
        week[i] = currentMonthDays[dayIndex++];
      }
      weeks.push(week);
    }
    
    return (
      <div className="overflow-hidden rounded-lg shadow-lg border border-gray-200 w-full">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-blue-700 to-indigo-800">
              <tr>
                <th className="px-1 py-2 md:px-3 md:py-4 text-center font-medium text-white uppercase tracking-wider border-r border-blue-600 text-xs">Sun</th>
                <th className="px-1 py-2 md:px-3 md:py-4 text-center font-medium text-white uppercase tracking-wider border-r border-blue-600 text-xs">Mon</th>
                <th className="px-1 py-2 md:px-3 md:py-4 text-center font-medium text-white uppercase tracking-wider border-r border-blue-600 text-xs">Tue</th>
                <th className="px-1 py-2 md:px-3 md:py-4 text-center font-medium text-white uppercase tracking-wider border-r border-blue-600 text-xs">Wed</th>
                <th className="px-1 py-2 md:px-3 md:py-4 text-center font-medium text-white uppercase tracking-wider border-r border-blue-600 text-xs">Thu</th>
                <th className="px-1 py-2 md:px-3 md:py-4 text-center font-medium text-white uppercase tracking-wider border-r border-blue-600 text-xs">Fri</th>
                <th className="px-1 py-2 md:px-3 md:py-4 text-center font-medium text-white uppercase tracking-wider text-xs">Sat</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {weeks.map((week, weekIndex) => (
                <tr key={`week-${weekIndex}`} className="divide-x divide-gray-200 transition-colors hover:bg-gray-50">
                  {week.map((day, dayIndex) => {
                    // Skip rendering for null cells
                    if (day === null) {
                      return (
                        <td 
                          key={`day-${weekIndex}-${dayIndex}`} 
                          className="p-1 text-center align-top bg-gray-50"
                        >
                          <div className="h-12 md:h-20 lg:h-28"></div>
                        </td>
                      );
                    }
                    
                    // Format date string for this day - only current month
                    const dayDateString = formatDateString(year, month, day);
                    
                    // Get institutes with trainings for this day
                    const dayInstitutes = dateInstitutesMap[dayDateString] || {};
                    const hasTrainings = Object.keys(dayInstitutes).length > 0;
                    
                    return (
                      <td 
                        key={`day-${weekIndex}-${dayIndex}`} 
                        className="p-1 text-center align-top transition-all duration-200"
                      >
                        <div className="h-12 md:h-20 lg:h-28 relative flex flex-col">
                          <div className="text-xs md:text-sm font-medium mb-1">{day}</div>
                          <div className="flex-grow overflow-y-auto">
                            <div className="space-y-1">
                            {hasTrainings && Object.entries(dayInstitutes).map(([institute, count]) => (
                              <div 
                                key={`${dayDateString}-${institute}`} 
                                className={`text-xs p-1 md:p-1.5 rounded-md cursor-pointer hover:opacity-90 transform hover:scale-105 transition-all duration-200 text-center ${getInstituteColorClass(institute)}`}
                                onClick={() => handleInstituteClick(institute, dayDateString, year, month, day)}
                                title={`${institute}: ${count} training(s)`}
                              >
                                <span className="font-semibold">{institute}</span>
                              </div>
                            ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 w-full">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4 mb-6">
        <div className="flex flex-col">
          <h3 className="text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-800">
            {getCurrentMonthYear()}
          </h3>
          {(selectedScheme || selectedInstitute) && (
            <div className="flex flex-wrap gap-2 mt-1">
              {selectedScheme && selectedScheme !== 'ALL' && (
                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                  Scheme: {selectedScheme}
                </span>
              )}
              {selectedInstitute && selectedInstitute !== 'ALL' && (
                <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                  Institute: {selectedInstitute}
                </span>
              )}
              {selectedScheme === 'ALL' && (
                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                  All Schemes
                </span>
              )}
              {selectedInstitute === 'ALL' && (
                <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                  All Institutes
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap justify-center sm:justify-end gap-3 bg-white px-3 py-2 rounded-lg shadow-sm w-full sm:w-auto">
          <div className="flex items-center space-x-2">
            <span className="inline-block w-4 h-4 rounded-full bg-gradient-to-r from-blue-400 to-blue-300 shadow-sm"></span>
            <span className="text-xs font-medium text-gray-700">SIR</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-block w-4 h-4 rounded-full bg-gradient-to-r from-green-500 to-green-400 shadow-sm"></span>
            <span className="text-xs font-medium text-gray-700">RIR</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-block w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-purple-400 shadow-sm"></span>
            <span className="text-xs font-medium text-gray-700">DRC</span>
          </div>
        </div>
      </div>
      
      {renderCalendar()}

      {/* Institute Trainings Modal - Shows when multiple trainings are available */}
      {modalIsOpen && showListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2 sm:p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            <div className="p-3 sm:p-5 border-b bg-gradient-to-r from-blue-600 to-indigo-700">
              <div className="flex justify-between items-center">
                <h3 className="text-base sm:text-lg font-semibold text-white">
                  {/* FIXED: Use renamed state variable */}
                  {clickedInstitute} Trainings on {selectedDate}
                </h3>
                <button 
                  onClick={handleCloseModal}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-3 sm:p-5 max-h-[50vh] overflow-y-auto">
              <ul className="divide-y divide-gray-200">
                {dateTrainings.map((training) => {
                  // Generate the proper training code
                  const trainingCode = training.institute && training.scheme && training.center && training.year && training.month && training.stitle && training.sno
                    ? `${training.institute}/${training.scheme}/${training.center}/${training.year}/${training.month}/${training.stitle}/${training.sno}`
                    : 'No code';
                  
                  return (
                    <li key={training.id} className="py-2 sm:py-3">
                      <button
                        onClick={() => handleSelectTraining(training)}
                        className="w-full text-left hover:bg-blue-50 p-2 sm:p-3 rounded-lg transition-colors"
                      >
                        <p className="font-medium text-blue-700 text-sm sm:text-base">{training.training}</p>
                        <p className="text-xs font-mono bg-gray-100 p-1 sm:p-2 rounded-md text-gray-600 mt-1 sm:mt-2 break-all">
                          {trainingCode}
                        </p>
                        <div className="flex justify-between items-center mt-2 sm:mt-3">
                          <p className="text-xs sm:text-sm text-gray-600">
                            <span className="font-semibold">Type:</span> {training.type}
                          </p>
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                            View Details
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            
            <div className="px-3 py-3 sm:px-5 sm:py-4 bg-gray-50 text-right border-t">
              <button
                onClick={handleCloseModal}
                className="inline-flex justify-center py-1.5 sm:py-2 px-3 sm:px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Training Details Modal */}
      {modalIsOpen && selectedTraining && (
        <TrainingDetailsModal 
          isOpen={modalIsOpen && selectedTraining !== null}
          training={selectedTraining}
          onClose={handleCloseModal}
          onApprove={handleApprove}
          onReject={handleReject}
          userRole={userRole}
        />
      )}
    </div>
  );
};

export default CalendarView;